// utils/auth.js
import axios from 'axios';
import config from '../config';

/** Refresh access this many seconds before JWT exp (aligned with isTokenExpired ~30s skew). */
export const PROACTIVE_REFRESH_BUFFER_SEC = 120;

let refreshInFlight = null;
let proactiveTimerId = null;
let proactiveLifecycleInitialized = false;

/**
 * Check if a JWT token is expired on the client side
 * @param {string} token - The JWT token
 * @returns {boolean} - True if token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    // Add 30 second buffer to account for clock skew
    return payload.exp < (currentTime + 30);
  } catch (error) {
    return true; // Assume expired if we can't parse
  }
};

/**
 * Milliseconds until access JWT exp, or -Infinity if invalid.
 */
export const getMsUntilAccessExpiry = (token) => {
  if (!token || !isValidTokenStructure(token)) return -Infinity;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (typeof payload.exp !== 'number') return -Infinity;
    return payload.exp * 1000 - Date.now();
  } catch {
    return -Infinity;
  }
};

/**
 * Validate JWT access token structure (three dot-separated segments).
 * @param {string} token - The JWT access token
 * @returns {boolean} - True if token has valid structure
 */
export const isValidTokenStructure = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every(part => part.length > 0);
};

/**
 * User id carried by the access token. Used only to scope client caches.
 * The server still decides access.
 */
export function readAccessUserId() {
  const token = localStorage.getItem('token');
  if (!isValidTokenStructure(token)) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const id = payload.userId || payload.sub || null;
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

/**
 * Server refresh tokens are opaque (e.g. hex), not JWTs — do not use JWT structure checks.
 * Rejects empty values and common bad localStorage states from setItem(undefined).
 */
export const isValidOpaqueRefreshToken = (token) => {
  if (token == null || typeof token !== 'string') return false;
  const t = token.trim();
  if (t.length === 0) return false;
  if (t === 'undefined' || t === 'null') return false;
  return true;
};

/**
 * Check if we have a valid token in localStorage
 * @returns {boolean} - True if we have a non-expired token
 */
export const hasValidToken = () => {
  const token = localStorage.getItem('token');
  return token && isValidTokenStructure(token) && !isTokenExpired(token);
};

export function cancelProactiveAccessRefresh() {
  if (proactiveTimerId !== null && typeof window !== 'undefined') {
    window.clearTimeout(proactiveTimerId);
    proactiveTimerId = null;
  }
}

/**
 * Schedule a single proactive refresh before access JWT expiry.
 * Idempotent: replaces any previous timer.
 */
export function scheduleProactiveAccessRefresh() {
  cancelProactiveAccessRefresh();
  if (typeof window === 'undefined') return;

  const token = localStorage.getItem('token');
  const refreshOpaque = localStorage.getItem('refreshToken');
  if (!token || !isValidTokenStructure(token) || !isValidOpaqueRefreshToken(refreshOpaque)) {
    return;
  }

  const bufferMs = PROACTIVE_REFRESH_BUFFER_SEC * 1000;
  const msUntilExp = getMsUntilAccessExpiry(token);

  if (msUntilExp <= 0) {
    void refreshAccessToken();
    return;
  }

  const delayMs = msUntilExp - bufferMs;
  if (delayMs <= 0) {
    void refreshAccessToken();
    return;
  }

  const cappedDelay = Math.min(delayMs, 7 * 86400000);

  proactiveTimerId = window.setTimeout(() => {
    proactiveTimerId = null;
    if (!isValidOpaqueRefreshToken(localStorage.getItem('refreshToken'))) return;
    void refreshAccessToken();
  }, cappedDelay);
}

/**
 * Attach focus / visibility listeners once so returning to the tab reschedules and can refresh early.
 */
export function initProactiveRefreshLifecycle() {
  if (proactiveLifecycleInitialized || typeof window === 'undefined') return;
  proactiveLifecycleInitialized = true;

  const onResume = () => {
    scheduleProactiveAccessRefresh();
    const token = localStorage.getItem('token');
    if (!token || !isValidOpaqueRefreshToken(localStorage.getItem('refreshToken'))) return;
    const msLeft = getMsUntilAccessExpiry(token);
    const bufferMs = PROACTIVE_REFRESH_BUFFER_SEC * 1000;
    if (msLeft <= bufferMs) {
      void refreshAccessToken();
    }
  };

  window.addEventListener('focus', onResume);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) onResume();
  });
}

/**
 * Attempt to refresh the access token using the refresh token
 * @returns {Promise<string|null>} - New access token or null if failed
 */
export const refreshAccessToken = async () => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!isValidOpaqueRefreshToken(refreshToken)) {
        clearAuthData();
        return null;
      }

      try {
        const response = await axios.post(`${config.apiBaseUrl}/api/auth/refresh-token`, {
          refreshToken,
        }, {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        // Validate new access token before storing (JWT from server)
        if (!accessToken || !isValidTokenStructure(accessToken)) {
          throw new Error('Invalid access token received');
        }
        
        localStorage.setItem('token', accessToken);
        
        // Rotated refresh token is opaque — same validation as initial storage
        if (isValidOpaqueRefreshToken(newRefreshToken)) {
          localStorage.setItem('refreshToken', newRefreshToken.trim());
        }
        
        scheduleProactiveAccessRefresh();
        return accessToken;
      } catch (error) {
        console.error('Token refresh failed:', error.response?.data || error.message);
        clearAuthData();
        return null;
      }
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = () => {
  cancelProactiveAccessRefresh();

  const keysToRemove = ['token', 'refreshToken', 'userEmail', 'role'];
  keysToRemove.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      // Silent fail for localStorage issues
    }
  });

  // Clear contacts cache and polling leadership for all users
  try {
    const keys = Object.keys(localStorage);
    const contactsCacheKeys = keys.filter(key =>
      key.startsWith('velox:contacts:') ||
      key.startsWith('velox:poll-leader:') ||
      key.startsWith('insights_') ||
      key.startsWith('refresh_count_')
    );
    contactsCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    // Silent fail for localStorage issues
  }
};
